import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import config from '../../config';
import s from './BookingJournal.module.scss';

const API = `${config.opsApiBase}/journeys`;

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDay(value) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'enquiry', label: 'Enquiry' },
  { id: 'draft', label: 'Draft' },
  { id: 'booking', label: 'Booking' },
];

function journeyKind(item) {
  if (item.booking_id) return 'booking';
  if (item.draft_booking_id) return 'draft';
  return 'enquiry';
}

export default function BookingJournal() {
  const history = useHistory();
  const [days, setDays] = useState([]);
  const [day, setDay] = useState('');
  const [openCount, setOpenCount] = useState(0);
  const [journeys, setJourneys] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [story, setStory] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingStory, setLoadingStory] = useState(false);
  const [error, setError] = useState('');
  const [kind, setKind] = useState('all');
  const [query, setQuery] = useState('');
  const pageRef = useRef(null);

  useEffect(() => {
    const text = query.trim();
    if (text.length === 1) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoadingList(true);
      setError('');
      try {
        const params = new URLSearchParams();
        if (text.length >= 2) params.set('q', text);
        else if (day) params.set('day', day);
        const res = await fetch(`${API}${params.toString() ? `?${params}` : ''}`);
        if (!res.ok) throw new Error('Could not load bookings');
        const data = await res.json();
        if (cancelled) return;
        setDays(data.days || []);
        setOpenCount(data.open_count || 0);
        setJourneys(data.journeys || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load bookings');
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    }, text.length >= 2 ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [day, query]);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const fit = () => {
      if (window.innerWidth <= 900) {
        page.style.height = 'auto';
        return;
      }
      const top = page.getBoundingClientRect().top;
      const scroller = page.closest('main');
      const bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
      const pad = scroller ? parseFloat(getComputedStyle(scroller).paddingBottom) || 0 : 28;
      page.style.height = `${Math.max(360, bottom - top - pad)}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  const visible = journeys.filter((item) => kind === 'all' || journeyKind(item) === kind);
  const counts = {
    all: journeys.length,
    enquiry: journeys.filter((item) => journeyKind(item) === 'enquiry').length,
    draft: journeys.filter((item) => journeyKind(item) === 'draft').length,
    booking: journeys.filter((item) => journeyKind(item) === 'booking').length,
  };

  useEffect(() => {
    const next = journeys.filter((item) => kind === 'all' || journeyKind(item) === kind);
    if (next.some((item) => item.id === selectedId)) return;
    const first = next[0];
    setSelectedId(first ? first.id : null);
    if (!first) setStory(null);
  }, [journeys, kind, selectedId]);

  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    setLoadingStory(true);
    fetch(`${API}/${selectedId}`)
      .then((res) => {
        if (!res.ok) throw new Error('Could not open this booking');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setStory(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not open this booking');
      })
      .finally(() => {
        if (!cancelled) setLoadingStory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  return (
    <div className={s.page} ref={pageRef}>
      <header className={s.header}>
        <div>
          <button className={s.back} onClick={() => history.push('/app/knowledge/documents')}>
            Back to Knowledge Base
          </button>
          <h1>Booking Journal</h1>
          <p>In progress shows every open booking, whichever day it started. Closed bookings are grouped by the day they were closed.</p>
        </div>
      </header>

      {error && <div className={s.error}>{error}</div>}

      <div className={s.days}>
        <button
          className={day ? s.day : s.dayActive}
          onClick={() => { setQuery(''); setDay(''); }}
        >
          <strong>In progress</strong>
          <span>{openCount} {openCount === 1 ? 'journey' : 'journeys'}</span>
        </button>
        {days.map((item) => (
          <button
            key={item.day}
            className={item.day === day ? s.dayActive : s.day}
            onClick={() => { setQuery(''); setDay(item.day); }}
          >
            <strong>{formatDay(item.day)}</strong>
            <span>{item.total} {item.total === 1 ? 'journey' : 'journeys'}</span>
          </button>
        ))}
      </div>

      <div className={s.layout}>
        <div className={s.filters}>
          <input
            className={s.search}
            value={query}
            placeholder="Booking, draft, lead, phone"
            onChange={(event) => setQuery(event.target.value)}
          />
          {FILTERS.map((item) => (
            <button
              key={item.id}
              className={kind === item.id ? s.filterActive : s.filter}
              onClick={() => setKind(item.id)}
            >
              <strong>{item.label}</strong>
              <span>{counts[item.id]}</span>
            </button>
          ))}
        </div>

        <aside className={s.list}>
          {loadingList && <p className={s.muted}>Loading bookings…</p>}
          {!loadingList && visible.length === 0 && (
            <p className={s.muted}>
              {query.trim().length >= 2
                ? 'Nothing matches that search.'
                : journeys.length === 0
                  ? (day ? 'Nothing was closed on this day.' : 'No bookings are in progress.')
                  : 'Nothing in this list matches that filter.'}
            </p>
          )}
          {visible.map((item) => (
            <button
              key={item.id}
              className={item.id === selectedId ? s.cardActive : s.card}
              onClick={() => setSelectedId(item.id)}
            >
              <div className={s.cardTop}>
                <strong>{item.title}</strong>
                <span className={item.status === 'closed' ? s.closed : s.open}>
                  {item.status === 'closed' ? 'Closed' : 'In progress'}
                </span>
              </div>
              <p>
                {item.event_count} step{item.event_count === 1 ? '' : 's'}
                {' · '}
                {item.status === 'closed' && item.closed_at
                  ? `Closed ${formatWhen(item.closed_at)}`
                  : formatWhen(item.opened_at)}
              </p>
              {item.last_outcome && <p className={s.outcome}>Latest: {String(item.last_outcome).replace(/_/g, ' ')}</p>}
              {(item.customer_phone || item.driver_phone) && (
                <p>
                  {item.customer_phone ? `Customer ${item.customer_phone}` : ''}
                  {item.customer_phone && item.driver_phone ? ' · ' : ''}
                  {item.driver_phone ? `Driver ${item.driver_phone}` : ''}
                </p>
              )}
            </button>
          ))}
        </aside>

        <section className={s.story}>
          {!story && !loadingStory && <p className={s.muted}>Select a booking to see its story.</p>}
          {loadingStory && <p className={s.muted}>Opening the story…</p>}
          {story && !loadingStory && (
            <>
              <div className={s.storyHead}>
                <div>
                  <h2>{story.title}</h2>
                  <p>
                    Started {formatWhen(story.opened_at)}
                    {story.closed_at ? ` · Closed ${formatWhen(story.closed_at)}` : ''}
                    {story.customer_name ? ` · ${story.customer_name}` : story.customer_id ? ` · Customer ${story.customer_id}` : ''}
                    {story.customer_phone ? ` · ${story.customer_phone}` : ''}
                  </p>
                </div>
                <span className={story.status === 'closed' ? s.closed : s.open}>
                  {story.status === 'closed' ? 'Closed' : 'In progress'}
                </span>
              </div>

              <div className={s.facts}>
                <div><span>Pickup</span><strong>{story.pickup || 'Not recorded yet'}</strong></div>
                <div><span>Drop</span><strong>{story.drops.length ? story.drops.join(' · ') : 'Not recorded yet'}</strong></div>
                <div><span>Vehicle</span><strong>{story.vehicle || 'Not recorded yet'}</strong></div>
                <div><span>Rate</span><strong>{story.rate != null ? `₹${story.rate}` : 'Not recorded yet'}</strong></div>
              </div>

              <ol className={s.timeline}>
                {story.steps.map((step, index) => (
                  <li key={step.id}>
                    <div className={s.marker}>{index + 1}</div>
                    <div className={s.step}>
                      <div className={s.stepTop}>
                        <h3>{step.heading}</h3>
                        <time>{formatWhen(step.at)}</time>
                      </div>
                      {step.details && step.details.length > 0 ? (
                        <dl className={s.details}>
                          {step.details.map((item) => (
                            <div key={item.label}>
                              <dt>{item.label}</dt>
                              <dd>{item.value}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <p>{step.sentence || 'No extra detail was stored for this step.'}</p>
                      )}
                      {step.summary && <p className={s.summary}>{step.summary}</p>}
                      {step.party && <span className={s.party}>{step.party}</span>}
                    </div>
                  </li>
                ))}
                {story.steps.length === 0 && <p className={s.muted}>No steps have been recorded yet.</p>}
              </ol>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
